// Orders list + Order detail strings — namespace `orders`
export default {
  en: {
    // OrdersPage
    title: 'My orders',
    itemCount: '{count} item | {count} items',
    noOrders: {
      body: "You haven't ordered anything yet.",
      cta: 'Browse catalog',
    },
    noMatch: 'No orders match this filter.',
    filterAriaLabel: 'Filter orders by status',
    filters: {
      all: 'All',
      active: 'Active',
      completed: 'Completed',
      failed: 'Failed',
      refunded: 'Refunded',
    },
    topup: {
      name: 'Wallet funds top-up',
      walletLabel: 'Wallet',
      refLabel: 'Ref #{ref}',
    },
    table: {
      item: 'Item',
      order: 'Order',
      date: 'Date',
      status: 'Status',
      total: 'Total',
      actions: 'Actions',
    },
    row: {
      qty: 'qty {qty}',
    },
    downloadInvoice: 'Download invoice',
    downloadInvoiceFailed: 'Could not download the invoice. Please try again.',
    status: {
      CREATED: 'Awaiting payment',
      PAID: 'Paid',
      PROCESSING: 'Processing',
      DELIVERED: 'Delivered',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      REFUNDED: 'Refunded',
    },
    statusSubtext: {
      COMPLETED_with_date: 'Delivered {date}',
      COMPLETED_no_date: 'Keys delivered',
      DELIVERED: 'Keys ready to reveal',
      PROCESSING: 'Fetching your keys from the supplier',
      PAID: 'Waiting on supplier',
      CREATED: 'Awaiting payment',
      FAILED_generic: 'Order failed',
      REFUNDED: 'Refund issued',
    },

    // OrderDetailPage
    backLink: 'My orders',
    loading: 'Loading…',
    notFound: 'Order not found',
    detail: {
      title: 'Order details',
      crumb: 'Order',
      qty: 'qty {qty} · {price} each',
      inFlight: {
        heading: "We're getting your keys from the supplier.",
        body: "This usually takes under a minute. You'll see them here as soon as they arrive — no need to refresh.",
      },
      failed: {
        label: 'Order failed',
        contactSupport: 'Contact support',
      },
      keys: {
        copiedToast: 'Key copied to clipboard',
        copyFailed: 'Copy failed',
        heading: 'Your keys',
        encryptedNote: 'Keys are encrypted at rest. Reveal to view — your access is logged.',
        reveal: 'Reveal keys',
        keyLabel: 'Key',
        copy: 'Copy',
        redemptionImageAlt: 'Redemption image',
      },
      timeline: {
        placed: 'Placed',
        paid: 'Paid',
        delivered: 'Delivered',
        log: 'Status history',
        heading: 'Timeline',
      },
      invoice: {
        heading: 'Invoice & VAT',
        seller: 'Seller',
        billedTo: 'Billed to',
        vatId: 'VAT ID {id}',
      },
      aside: {
        buyAgain: 'Buy again',
        orderLabel: 'Order',
        copyOrderId: 'Copy order ID',
        orderIdCopied: 'Order ID copied',
        placed: 'Placed',
        completed: 'Completed',
        quantity: 'Quantity',
        unitPrice: 'Unit price',
        total: 'Total',
        needHelp: 'Need help with this order?',
        supportRef: 'Support ref: {ref}',
      },
    },
  },

  cs: {
    // OrdersPage
    title: 'Moje objednávky',
    itemCount: '{count} položka | {count} položky | {count} položek',
    noOrders: {
      body: 'Zatím jste nic neobjednali.',
      cta: 'Procházet katalog',
    },
    noMatch: 'Žádné objednávky neodpovídají tomuto filtru.',
    filterAriaLabel: 'Filtrovat objednávky podle stavu',
    filters: {
      all: 'Vše',
      active: 'Aktivní',
      completed: 'Dokončené',
      failed: 'Neúspěšné',
      refunded: 'Vrácené',
    },
    topup: {
      name: 'Dobití peněženky',
      walletLabel: 'Peněženka',
      refLabel: 'Ref #{ref}',
    },
    table: {
      item: 'Položka',
      order: 'Objednávka',
      date: 'Datum',
      status: 'Stav',
      total: 'Celkem',
      actions: 'Akce',
    },
    row: {
      qty: 'ks {qty}',
    },
    downloadInvoice: 'Stáhnout fakturu',
    downloadInvoiceFailed: 'Fakturu se nepodařilo stáhnout. Zkuste to prosím znovu.',
    status: {
      CREATED: 'Čeká na platbu',
      PAID: 'Zaplaceno',
      PROCESSING: 'Zpracovává se',
      DELIVERED: 'Doručeno',
      COMPLETED: 'Dokončeno',
      FAILED: 'Selhalo',
      REFUNDED: 'Vráceno',
    },
    statusSubtext: {
      COMPLETED_with_date: 'Doručeno {date}',
      COMPLETED_no_date: 'Klíče doručeny',
      DELIVERED: 'Klíče připraveny k zobrazení',
      PROCESSING: 'Načítáme vaše klíče od dodavatele',
      PAID: 'Čekání na dodavatele',
      CREATED: 'Čeká na platbu',
      FAILED_generic: 'Objednávka selhala',
      REFUNDED: 'Vrácení provedeno',
    },

    // OrderDetailPage
    backLink: 'Moje objednávky',
    loading: 'Načítání…',
    notFound: 'Objednávka nenalezena',
    detail: {
      title: 'Detail objednávky',
      crumb: 'Objednávka',
      qty: 'ks {qty} · {price} za kus',
      inFlight: {
        heading: 'Načítáme vaše klíče od dodavatele.',
        body: 'To obvykle trvá méně než minutu. Klíče se zobrazí zde, jakmile dorazí — není třeba obnovovat stránku.',
      },
      failed: {
        label: 'Objednávka selhala',
        contactSupport: 'Kontaktovat podporu',
      },
      keys: {
        copiedToast: 'Klíč zkopírován do schránky',
        copyFailed: 'Kopírování se nezdařilo',
        heading: 'Vaše klíče',
        encryptedNote: 'Klíče jsou šifrovány. Kliknutím je zobrazíte — přístup je zaznamenán.',
        reveal: 'Zobrazit klíče',
        keyLabel: 'Klíč',
        copy: 'Kopírovat',
        redemptionImageAlt: 'Obrázek pro uplatnění',
      },
      timeline: {
        placed: 'Vytvořeno',
        paid: 'Zaplaceno',
        delivered: 'Doručeno',
        log: 'Historie stavů',
        heading: 'Průběh objednávky',
      },
      invoice: {
        heading: 'Faktura a DPH',
        seller: 'Prodávající',
        billedTo: 'Fakturováno na',
        vatId: 'DIČ {id}',
      },
      aside: {
        buyAgain: 'Koupit znovu',
        orderLabel: 'Objednávka',
        copyOrderId: 'Kopírovat ID objednávky',
        orderIdCopied: 'ID objednávky zkopírováno',
        placed: 'Zadáno',
        completed: 'Dokončeno',
        quantity: 'Počet',
        unitPrice: 'Cena za kus',
        total: 'Celkem',
        needHelp: 'Potřebujete pomoc s touto objednávkou?',
        supportRef: 'Reference podpory: {ref}',
      },
    },
  },
};
